import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Download, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { exportApi } from '../../lib/api';

const FORMATS = [
    { value: 'geojson' as const, label: 'GeoJSON (.geojson)' },
    { value: 'shapefile' as const, label: 'Shapefile (.zip)' },
    { value: 'kml' as const, label: 'KML (.kml)' },
    { value: 'gpkg' as const, label: 'GeoPackage (.gpkg)' },
];

const MENU_HEIGHT = 176; // approx height of 4 items (4 * 36px + 2*8px padding)
const MENU_WIDTH = 200;

interface PolygonDownloadMenuProps {
    polygonId: string;
    polygonName?: string;
    fullWidth?: boolean;
}

export default function PolygonDownloadMenu({ polygonId, polygonName, fullWidth }: PolygonDownloadMenuProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const buttonRef = useRef<HTMLButtonElement>(null);

    const computePosition = useCallback(() => {
        if (!buttonRef.current) return;
        const rect = buttonRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;

        const top = spaceBelow >= MENU_HEIGHT + 4
            ? rect.bottom + 4
            : rect.top - MENU_HEIGHT - 4;

        // Clamp so the menu never overflows the viewport (8px margin)
        const left = Math.max(8, Math.min(
            rect.right - MENU_WIDTH,
            window.innerWidth - MENU_WIDTH - 8
        ));

        setPos({ top, left });
    }, []);

    const toggleOpen = () => {
        if (!open) computePosition();
        setOpen((v) => !v);
    };

    // Close on scroll or resize
    useEffect(() => {
        if (!open) return;
        const close = () => setOpen(false);
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [open]);

    const handleDownload = async (format: 'geojson' | 'shapefile' | 'kml' | 'gpkg') => {
        setDownloading(true);
        setOpen(false);
        try {
            await exportApi.downloadPolygons(format, { polygon_ids: polygonId });
            toast.success(t('common.downloadedAs', { name: polygonName || t('common.polygon', { defaultValue: 'Polygon' }), format: format.toUpperCase() }));
        } catch {
            toast.error(t('common.downloadFailed'));
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className={fullWidth ? 'relative w-full' : 'relative inline-flex'}>
            <button
                ref={buttonRef}
                onClick={toggleOpen}
                disabled={downloading}
                className={
                    fullWidth
                        ? 'w-full flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm text-gray-600 dark:text-gray-400'
                        : 'flex items-center gap-2 text-sm text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors'
                }
            >
                {downloading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                    <Download className="w-4 h-4" />
                )}
                {t('common.download')}
            </button>
            {open && createPortal(
                <>
                    <div className="fixed inset-0 z-[9998]" onClick={() => setOpen(false)} />
                    <div
                        className="fixed z-[9999] bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1"
                        style={{ top: pos.top, left: pos.left, minWidth: MENU_WIDTH }}
                    >
                        {FORMATS.map(({ value, label }) => (
                            <button
                                key={value}
                                onClick={() => handleDownload(value)}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </>,
                document.body
            )}
        </div>
    );
}
